import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { X, Radio, Calendar, Bell } from 'lucide-react'
import { cn } from '@/lib/utils'
import { useRoute } from '@/lib/RouteContext'

/** "in 2 hours" / "em 2 horas" in the reader's language, without a date library locale. */
const relativeStart = (startTime: string, language: string) => {
  const ms = new Date(startTime).getTime() - Date.now()
  const minutes = Math.round(ms / 60_000)
  const format = new Intl.RelativeTimeFormat(language, { numeric: 'auto' })
  if (Math.abs(minutes) < 60) return format.format(minutes, 'minute')
  /* Rounded from the milliseconds, like the reminder title, so "event in 6 h" never reads "in 7 hours". */
  const hours = Math.round(ms / 3_600_000)
  return Math.abs(hours) < 48 ? format.format(hours, 'hour') : format.format(Math.round(hours / 24), 'day')
}

interface EventToastProps {
  id: string
  type: 'live_started' | 'upcoming' | 'reminder'
  event: {
    id: string
    title: string
    startTime: string
    bannerUrl?: string | null
  }
  onClose: () => void
  autoClose?: boolean
  autoCloseDelay?: number
}

export function EventToast({
  type,
  event,
  onClose,
  autoClose = true,
  autoCloseDelay = 8000
}: EventToastProps) {
  const [isExiting, setIsExiting] = useState(false)
  const { t, i18n } = useTranslation('events')
  const { navigate, params } = useRoute()

  useEffect(() => {
    if (autoClose) {
      const timer = setTimeout(() => {
        handleClose()
      }, autoCloseDelay)

      return () => clearTimeout(timer)
    }
  }, [autoClose, autoCloseDelay])

  const handleClose = () => {
    setIsExiting(true)
    setTimeout(() => {
      onClose()
    }, 300)
  }

  const handleClick = () => {
    /* The toast only shows inside an Events space, so the event page is under the current route. */
    if (params.sectionSlug && params.spaceSlug) navigate(`/${params.sectionSlug}/${params.spaceSlug}/${event.id}`)
    handleClose()
  }

  const getIcon = () => {
    switch (type) {
      case 'live_started':
        return <Radio className="w-5 h-5 animate-pulse" />
      case 'upcoming':
        return <Calendar className="w-5 h-5" />
      case 'reminder':
        return <Bell className="w-5 h-5" />
    }
  }

  const getTitle = () => {
    switch (type) {
      case 'live_started':
        return t('toast.liveTitle')
      case 'upcoming':
        return t('toast.upcomingTitle')
      case 'reminder': {
        const eventDate = new Date(event.startTime)
        const hoursUntil = Math.round((eventDate.getTime() - new Date().getTime()) / (1000 * 60 * 60))
        return t('toast.reminderTitle', { count: hoursUntil })
      }
    }
  }

  const getColorClasses = () => {
    switch (type) {
      case 'live_started':
        return 'bg-primary-600 text-white'
      case 'upcoming':
        return 'bg-primary-600 text-white'
      case 'reminder':
        return 'bg-warning text-white'
    }
  }

  return (
    <div
      className={cn(
        'fixed bottom-6 right-6 z-[100] w-96 max-w-[calc(100vw-3rem)] lg:bottom-[calc(var(--chat-dock-bar-h,0px)+1.5rem)]',
        'shadow-2xl rounded-xl overflow-hidden',
        'transform transition-all duration-300 ease-out',
        isExiting
          ? 'translate-x-[120%] opacity-0'
          : 'translate-x-0 opacity-100 animate-in slide-in-from-right-5'
      )}
    >
      <div className={cn('relative', getColorClasses())}>
        <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+PGRlZnM+PHBhdHRlcm4gaWQ9ImdyaWQiIHdpZHRoPSI2MCIgaGVpZ2h0PSI2MCIgcGF0dGVyblVuaXRzPSJ1c2VyU3BhY2VPblVzZSI+PHBhdGggZD0iTSAxMCAwIEwgMCAwIDAgMTAiIGZpbGw9Im5vbmUiIHN0cm9rZT0icmdiYSgyNTUsMjU1LDI1NSwwLjEpIiBzdHJva2Utd2lkdGg9IjEiLz48L3BhdHRlcm4+PC9kZWZzPjxyZWN0IHdpZHRoPSIxMDAlIiBoZWlnaHQ9IjEwMCUiIGZpbGw9InVybCgjZ3JpZCkiLz48L3N2Zz4=')] opacity-30" />

        <div className="relative p-4">
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 rounded-full bg-white/20 backdrop-blur-sm flex items-center justify-center flex-shrink-0">
              {getIcon()}
            </div>

            <div className="flex-1 min-w-0 cursor-pointer" onClick={handleClick}>
              <p className="font-bold text-sm uppercase tracking-wider mb-1">
                {getTitle()}
              </p>
              <p className="font-semibold text-base mb-1 line-clamp-2">
                {event.title}
              </p>
              <p className="text-xs opacity-90">
                {type === 'live_started' ? (
                  t('toast.clickToWatch')
                ) : (
                  t('toast.starts', { when: relativeStart(event.startTime, i18n.language) })
                )}
              </p>
            </div>

            <button
              onClick={(e) => {
                e.stopPropagation()
                handleClose()
              }}
              className="w-8 h-8 rounded-full hover:bg-white/20 transition-colors flex items-center justify-center flex-shrink-0"
              aria-label={t('toast.close')}
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          <div className="mt-3 h-1 bg-white/20 rounded-full overflow-hidden">
            <div
              className="h-full bg-white/60"
              style={{
                width: '100%',
                animation: autoClose ? 'toast-shrink 8s linear forwards' : 'none'
              }}
            />
          </div>
        </div>
      </div>

      <style dangerouslySetInnerHTML={{
        __html: `
          @keyframes toast-shrink {
            from { width: 100%; }
            to { width: 0%; }
          }
        `
      }} />
    </div>
  )
}
